import clsx from "clsx";
import { FC } from "react";

import { EvalResults, EvalSpec } from "@tsmono/inspect-common/types";
import { CopyButton } from "@tsmono/react/components";
import { filename } from "@tsmono/util";

import { EvalLogStatus } from "../../../@types/extraInspect";
import { useAbsLogDir, useLogDir } from "../../../app_config";
import { RunningMetric } from "../../../client/api/types";
import { DownloadLogButton } from "../../../components/DownloadLogButton";
import { kModelNone } from "../../../constants";
import { resolveHeadlineMetric } from "../../../scoring/headline";
import { toDisplayScorers } from "../../../scoring/metrics";
import { useEffectiveEvalConfig } from "../../../state/hooks";
import { useStore } from "../../../state/store";
import { useCurrentLogFile } from "../../routing/currentSelection";

import { ModelRolesView } from "./ModelRolesView";
import styles from "./PrimaryBar.module.css";
import { displayScorersFromRunningMetrics, ResultsPanel } from "./ResultsPanel";
import { RunningStatusPanel } from "./RunningStatusPanel";
import { CancelledPanel, ErroredPanel } from "./StatusPanel";
import { TagsField } from "./TagsField";

interface PrimaryBarProps {
  status?: EvalLogStatus;
  evalResults?: EvalResults | null;
  runningMetrics?: RunningMetric[];
  evalSpec?: EvalSpec;
  sampleCount?: number;
  tags?: string[];
}

export const PrimaryBar: FC<PrimaryBarProps> = ({
  status,
  evalResults,
  runningMetrics,
  evalSpec,
  sampleCount,
  tags,
}) => {
  const streamSamples = useStore((state) => state.capabilities.streamSamples);
  // Effective (folded) config: a mid-run change to continue_on_fail decides
  // how an errored run's status renders.
  const effectiveConfig = useEffectiveEvalConfig();
  const downloadLogs = useStore((state) => state.capabilities.downloadLogs);
  const absLogDir = useAbsLogDir();
  const selectedLogFile = useCurrentLogFile();
  const logDir = useLogDir();
  const logFileName = selectedLogFile ? filename(selectedLogFile) : "";
  const isEvalFile = selectedLogFile?.endsWith(".eval");
  const tagList = tags ?? [];

  const copyValue = (() => {
    if (!absLogDir || !selectedLogFile || !logDir) return selectedLogFile;
    const prefix = logDir + "/";
    const relFile = selectedLogFile.startsWith(prefix)
      ? selectedLogFile.slice(prefix.length)
      : selectedLogFile;
    return absLogDir.replace(/\/?$/, "/") + relFile;
  })();

  const hasRunningMetrics = runningMetrics && runningMetrics.length > 0;

  return (
    <div className={clsx(styles.wrapper)}>
      <div
        className={clsx(
          "navbar-brand",
          "navbar-text",
          "mb-0",
          styles.container
        )}
      >
        <div className={styles.body}>
          <div className={styles.bodyContainer}>
            <div
              id="task-title"
              className={clsx("task-title", styles.taskTitle)}
              title={evalSpec?.task}
            >
              {evalSpec?.task}
            </div>
            {evalSpec?.model && evalSpec.model !== kModelNone ? (
              <div
                id="task-model"
                className={clsx(
                  "task-model",
                  "text-truncate",
                  styles.taskModel,
                  "text-size-base"
                )}
                title={evalSpec.model}
              >
                {evalSpec.model}
              </div>
            ) : (
              ""
            )}
            <TagsField
              tags={tagList}
              className={styles.tagRowHeader}
              collapseOnWrap
            />
          </div>
          {evalSpec?.model_roles ? (
            <ModelRolesView roles={evalSpec.model_roles} />
          ) : undefined}

          <div className={clsx("text-size-small", styles.secondaryContainer)}>
            <div className={clsx("navbar-secondary-text", "text-truncate")}>
              {logFileName}
            </div>
            <div className={styles.buttonGroup}>
              {copyValue ? <CopyButton value={copyValue} /> : ""}
              {downloadLogs && selectedLogFile && isEvalFile ? (
                <DownloadLogButton log_file={selectedLogFile} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className={clsx(styles.taskStatus, "navbar-text")}>
        {status === "success" ||
        (status === "started" && streamSamples && hasRunningMetrics) ||
        (status === "error" && effectiveConfig?.continue_on_fail) ? (
          <ResultsPanel
            scorers={
              runningMetrics
                ? displayScorersFromRunningMetrics(runningMetrics)
                : toDisplayScorers(
                    evalResults?.scores,
                    resolveHeadlineMetric(
                      evalResults,
                      evalSpec?.headline_metric
                    )
                  )
            }
            headlineDeclared={evalSpec?.headline_metric != null}
          />
        ) : undefined}
        {status === "cancelled" ? (
          <CancelledPanel sampleCount={sampleCount || 0} />
        ) : undefined}
        {status === "started" && (!streamSamples || !hasRunningMetrics) ? (
          <RunningStatusPanel sampleCount={sampleCount || 0} />
        ) : undefined}
        {status === "error" ? (
          <ErroredPanel sampleCount={sampleCount || 0} />
        ) : undefined}
      </div>
      <div id="task-created" style={{ display: "none" }}>
        {evalSpec?.created}
      </div>
    </div>
  );
};
